import { useState } from 'react';

import { api } from '../../../services/api';
import type { Chat, Message } from '../../../types';

type Props = {
  activeChat: Chat | null;
  messages: Message[];
};

const DataExtractorPanel = ({ activeChat, messages }: Props) => {
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExtract = async () => {
    if (!activeChat || messages.length === 0) return;
    setExtracting(true);
    setResult(null);
    setError(null);
    try {
      const conversationMessages = messages.map((message) => ({
        role: (message.direction === 'inbound' ? 'user' : 'assistant') as 'user' | 'assistant',
        content: message.content,
      }));
      const output = await api.aiTask({
        task: 'extract',
        messages: conversationMessages,
        contactId: activeChat.id,
        contactName: activeChat.title,
      });
      setResult(output.result.content);
    } catch {
      setError('Extractie mislukt. Controleer of de AI provider is geconfigureerd.');
    } finally {
      setExtracting(false);
    }
  };

  if (!activeChat) {
    return (
      <div className="data-extractor-panel">
        <p className="tool-description">
          Selecteer een chat om contactgegevens en actiepunten uit het gesprek te extraheren.
        </p>
        <p className="empty-state small">Geen chat geselecteerd.</p>
      </div>
    );
  }

  return (
    <div className="data-extractor-panel">
      <p className="tool-description">
        Extraheer belangrijke feiten, datums en actiepunten uit het gesprek met{' '}
        <strong>{activeChat.title}</strong>.
      </p>
      <button
        type="button"
        className="button primary small"
        disabled={extracting || messages.length === 0}
        onClick={handleExtract}
      >
        {extracting ? 'Extraheren…' : 'Extractie starten'}
      </button>
      {error && <p className="notice error small">{error}</p>}
      {result && (
        <div className="extract-result">
          <h4>Geëxtraheerd</h4>
          <pre>{result}</pre>
        </div>
      )}
    </div>
  );
};

export default DataExtractorPanel;
